// src/api/aimode-backup.ts
//
// Admin-only: the SerpApi backup provider for the Google AI Mode lane (#89).
// The key goes up with a PUT and never comes back down — reads only say
// whether one is stored, its last four characters, and how it validated.
import { apiFetch } from "./api-fetch";

export interface AiModeBackupAccount {
  account_status?: string;
  account_email?: string;
  plan_name?: string;
  plan_monthly_price?: number;
  searches_per_month?: number;
  total_searches_left?: number;
  account_rate_limit_per_hour?: number;
  plan_cost_per_search_usd?: number | null;
}

export interface AiModeBackupValidation {
  ok: boolean;
  message: string;
  checked_at: string;
  account: AiModeBackupAccount;
}

export interface AiModeBackupSettings {
  provider: "serpapi";
  /** The admin's switch. */
  enabled: boolean;
  key_set: boolean;
  key_last4: string | null;
  /** The stored key passed its most recent validation. */
  validated: boolean;
  validation: AiModeBackupValidation | null;
  /** Switched on AND validated: failed DataForSEO calls go to SerpApi. */
  active: boolean;
  /** Admin override; null means the plan's price is used. */
  cost_per_search_usd: number | null;
  effective_cost_per_search_usd: number | null;
  limits: { min_cost_per_search_usd: number; max_cost_per_search_usd: number };
}

/** Any field omitted is left as it is. `cost_per_search_usd: null` clears the
 *  override. A new `api_key` is validated before it is stored. */
export interface AiModeBackupUpdate {
  enabled?: boolean;
  api_key?: string;
  clear_key?: boolean;
  cost_per_search_usd?: number | null;
}

/** One brand's choice: the platform's backup, or the brand's own SerpApi key. */
export interface BrandAiModeBackupSettings {
  brand_name: string;
  provider: "serpapi";
  /** "global" uses the platform backup; "own" uses this brand's key. */
  mode: "global" | "own";
  key_set: boolean;
  key_last4: string | null;
  validated: boolean;
  validation: AiModeBackupValidation | null;
  /** Which key this brand's failed AI Mode calls go to right now. */
  in_use: "brand" | "global" | null;
  global_active: boolean;
  cost_per_search_usd: number | null;
  effective_cost_per_search_usd: number | null;
  limits: { min_cost_per_search_usd: number; max_cost_per_search_usd: number };
}

export interface BrandAiModeBackupUpdate {
  mode?: "global" | "own";
  api_key?: string;
  clear_key?: boolean;
  cost_per_search_usd?: number | null;
}

const brandBase = (brand: string) =>
  `/admin/brands/${encodeURIComponent(brand)}/aimode-backup`;

export const brandAimodeBackupApi = {
  get: (brand: string) => apiFetch<BrandAiModeBackupSettings>(brandBase(brand)),

  update: (brand: string, changes: BrandAiModeBackupUpdate) =>
    apiFetch<BrandAiModeBackupSettings>(brandBase(brand), {
      method: "PUT",
      body: JSON.stringify(changes),
    }),

  validate: (brand: string) =>
    apiFetch<BrandAiModeBackupSettings>(`${brandBase(brand)}/validate`, {
      method: "POST",
    }),
};

export const aimodeBackupApi = {
  get: () => apiFetch<AiModeBackupSettings>("/admin/aimode-backup"),

  update: (changes: AiModeBackupUpdate) =>
    apiFetch<AiModeBackupSettings>("/admin/aimode-backup", {
      method: "PUT",
      body: JSON.stringify(changes),
    }),

  validate: () =>
    apiFetch<AiModeBackupSettings>("/admin/aimode-backup/validate", {
      method: "POST",
    }),
};
